import React, { useEffect, useState } from 'react';
import { supportFetch, fmtDate } from '../api';
import ExportButton from '../components/ExportButton';

const TYPES = [
  '', 'upload.start', 'upload.success', 'upload.error',
  'sftp.upload.success', 'sftp.upload.error',
  'download.success', 'download.denied',
  'auth.login.success', 'auth.login.failure',
  'auth.admin.login.success', 'auth.admin.login.failure',
  'auth.support.login.success', 'auth.support.login.failure',
  'system.error',
];

// Structured event-log viewer: filter, paginate, expand a row to its detail JSON.
const LogsTab = ({ token, onAuthFail }) => {
  const [filters, setFilters] = useState({ level: '', type: '', company: '', search: '', from: '', to: '' });
  const [events, setEvents]   = useState([]);
  const [cursor, setCursor]   = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [expanded, setExpanded] = useState(null);

  const buildQuery = (cur) => {
    const p = new URLSearchParams();
    if (filters.level)   p.set('level', filters.level);
    if (filters.type)    p.set('type', filters.type);
    if (filters.company) p.set('company', filters.company);
    if (filters.search)  p.set('search', filters.search);
    if (filters.from)    p.set('from', new Date(filters.from).toISOString());
    if (filters.to)      p.set('to', new Date(filters.to).toISOString());
    p.set('limit', '100');
    if (cur) p.set('cursor', cur);
    return p.toString();
  };

  const load = (reset) => {
    setLoading(true); setError('');
    const cur = reset ? undefined : cursor;
    supportFetch(`/support/logs?${buildQuery(cur)}`, token, onAuthFail)
      .then(d => {
        setEvents(reset ? d.events : prev => [...prev, ...d.events]);
        setCursor(d.continuationToken);
      })
      .catch(e => { if (e.message !== 'Session expired') setError('Failed to load logs.'); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const apply = (e) => { e.preventDefault(); setCursor(undefined); load(true); };

  return (
    <div className="sp-logs">
      <form className="sp-filterbar" onSubmit={apply}>
        <select value={filters.level} onChange={e => set('level', e.target.value)}>
          <option value="">All levels</option><option value="info">info</option><option value="warn">warn</option><option value="error">error</option>
        </select>
        <select value={filters.type} onChange={e => set('type', e.target.value)}>
          {TYPES.map(t => <option key={t} value={t}>{t || 'All types'}</option>)}
        </select>
        <input placeholder="Company" value={filters.company} onChange={e => set('company', e.target.value)} />
        <input placeholder="Search message…" value={filters.search} onChange={e => set('search', e.target.value)} />
        <label className="sp-date">From <input type="datetime-local" value={filters.from} onChange={e => set('from', e.target.value)} /></label>
        <label className="sp-date">To <input type="datetime-local" value={filters.to} onChange={e => set('to', e.target.value)} /></label>
        <button className="sp-btn" type="submit">Apply</button>
        <ExportButton rows={events} name="logs" />
      </form>

      {error && <div className="sp-error">{error}</div>}

      <table className="sp-table sp-table--compact">
        <thead><tr><th>Time</th><th>Level</th><th>Type</th><th>Company</th><th>Message</th></tr></thead>
        <tbody>
          {events.map((e, i) => (
            <React.Fragment key={i}>
              <tr className={`sp-lvl-${e.level} sp-clickable`} onClick={() => setExpanded(expanded === i ? null : i)}>
                <td>{fmtDate(e.timestamp)}</td>
                <td><span className={`sp-badge sp-badge--${e.level}`}>{e.level}</span></td>
                <td>{e.type}</td>
                <td>{e.company || '—'}</td>
                <td>{e.message}</td>
              </tr>
              {expanded === i && (
                <tr className="sp-detail-row"><td colSpan={5}>
                  <pre className="sp-json">{JSON.stringify(e, null, 2)}</pre>
                </td></tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      {!loading && events.length === 0 && !error && <div className="sp-empty">No events match.</div>}
      {loading && <div className="sp-loading">Loading…</div>}

      <div className="sp-toolbar">
        {cursor && !loading && <button className="sp-btn" onClick={() => load(false)}>Load more</button>}
        <span className="sp-muted">{events.length} event{events.length !== 1 ? 's' : ''} loaded</span>
      </div>
    </div>
  );
};

export default LogsTab;
